import * as preact from "preact";

import type { SignInMethod } from "appStorePurchases";
import { type PreactPageProps } from "pages/PreactPage";
import stores from "stores/stores";
import { SignupComponent } from "views/SignupView";

interface Account {
  email: string;
  /** Undefined for no restriction */
  signInMethods?: SignInMethod[];
}

interface State {
  signingIn: boolean;
  account?: Account;
}

/**
 * The account to sign in to, and its ways of signing in, when the app sends
 * the user here to use the subscription their Apple Account pays for
 * (appStorePurchases.ts)
 */
const signInAs = (): Account | undefined => {
  const params = new URLSearchParams(window.location.search);
  const email = params.get("signInAs");
  if (!email) {
    return undefined;
  }
  const signInMethods = (params.get("methods") ?? "")
    .split(",")
    .filter(Boolean) as SignInMethod[];
  return {
    email,
    signInMethods: signInMethods.length > 0 ? signInMethods : undefined,
  };
};

/**
 * The iOS app's front door for signed-out users (served at "/" in place of the
 * marketing landing page): a short pitch, then either the /start onboarding
 * flow or sign-in for returning users.
 */
export default class AppWelcomePageView extends preact.Component<
  PreactPageProps,
  State
> {
  state: State = (() => {
    const account = signInAs();
    return { signingIn: Boolean(account), account };
  })();

  /** Back to the start, forgetting any account the app named */
  back = () => {
    if (this.state.account) {
      window.history.replaceState({}, "", "/");
    }
    this.setState({ signingIn: false, account: undefined });
  };

  getStarted = () => {
    this.props.appRouter.navigate("/start", { trigger: true });
  };

  render() {
    const { account } = this.state;
    if (this.state.signingIn) {
      return (
        <div class="appWelcome appWelcomeSignIn">
          <button class="appWelcomeBack" onClick={this.back}>
            <i class="icon-chevron-left" /> Back
          </button>
          <h1>Welcome back</h1>
          {account ? <p>Sign in as {account.email}</p> : null}
          <SignupComponent
            stores={stores}
            initialMode="signin"
            email={account?.email}
            signInMethods={account?.signInMethods}
          />
        </div>
      );
    }

    return (
      <div class="appWelcome">
        <img
          class="appWelcomeRabbit"
          src="/content/landingPages/rabbit-reading.webp"
          alt=""
          width="1254"
          height="1254"
        />
        <h1>Learn a language reading what you love</h1>
        <p>
          Tap any word to translate it, then review new words with flashcards.
        </p>
        <div class="appWelcomeActions">
          <button class="prominent payButton" onClick={this.getStarted}>
            Get started
          </button>
          <button
            class="payButton lessProminent"
            onClick={() => this.setState({ signingIn: true })}
          >
            I already have an account
          </button>
        </div>
      </div>
    );
  }
}
